import { createAsyncThunk } from "@reduxjs/toolkit";
import { pendingGeometriesSliceName } from "../slices/pendingGeometriesSlice";

export interface GeometryFileParams {
  taskId: string | undefined;
  geometryId: string;
  fileName: string;
  url: string;
}

export interface ExportGeometryFileParams extends GeometryFileParams {
  fileHandle: FileSystemFileHandle;
}

export interface ImportGeometryFileParams extends GeometryFileParams {
  file: File;
}

export const downloadGeometryFile = createAsyncThunk(
  `${pendingGeometriesSliceName}/downloadGeometryFile`,
  ({ url, fileHandle }: ExportGeometryFileParams) =>
    retryPromise(() => (
      fetch(url, { method: "GET" })
        .then(ensureResponseOk)
        .then((response) => response.blob())
        .then((blob) => (
          fileHandle.createWritable()
            .then((writeStream) => (
              writeStream.write(blob).then(() => writeStream.close())
            ))
        ))
        .catch(logError("Error downloading geometry file."))
        .then()
    )),
);

export const uploadGeometryFile = createAsyncThunk(
  `${pendingGeometriesSliceName}/uploadGeometryFile`,
  ({ url, file }: ImportGeometryFileParams) =>
    retryPromise(() => (
      fetch(url, { method: "PUT", body: file })
        .then(ensureResponseOk)
        .catch(logError("Error uploading geometry file."))
        .then()
    )),
);

async function ensureResponseOk(response: Response): Promise<Response> {
  if (!response.ok) return Promise.reject(`Request failed: status=${response.status}, ${response.statusText}.`);
  return Promise.resolve(response);
}

function logError(errorMessage: string): (reason: unknown) => Promise<never> {
  return (reason: unknown): Promise<never> => {
    console.error(errorMessage, reason);
    return Promise.reject(reason);
  };
}

// 2 retries with a 1-second delay in between, so 3 calls in total
async function retryPromise<T>(promise: () => Promise<T>, retries = 2, delay = 1_000): Promise<T> {
  return promise()
    .catch(async (error) => {
      if (retries <= 0) {
        return Promise.reject(error);
      }

      return delayPromise(delay)
        .then(() => (
          retryPromise(promise, retries - 1, delay)
        ));
    });
}

function delayPromise(delay: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, delay);
  });
}
